import { useQueryClient } from "@tanstack/react-query";
import type Admin from "@types_def/models/admin.types";
import type { UserRank } from "@types_def/models/user.type";
import { t } from "i18next";
import { toast } from "react-toastify";

import {
  useDeleteAdminMutation,
  useFindAllAdminsQuery,
  useUpdateAdminMutation,
} from "@/libs/generated/hooks";

// Hooks
function useUpdateAdmin() {
  const client = useQueryClient();
  const mutation = useUpdateAdminMutation({
    onSuccess: (data) => {
      void client.invalidateQueries({ queryKey: ["admins", Number(data?.rank)] });
      toast(t("network.managers.updated"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (e: unknown) => {
      toast(
        e instanceof Error
          ? (e.message?.split(": ")[1] ?? t("network.errors.unknown"))
          : t("network.errors.unknown"),
        {
          position: "bottom-right",
          type: "error",
        }
      );
    },
  });

  return {
    ...mutation,
    mutate: (data: Admin & { id: number }) => mutation.mutate({ UpdateAdminInput: data }),
  };
}

function useDeleteAdmin() {
  const client = useQueryClient();
  const mutation = useDeleteAdminMutation({
    onSuccess: (_data) => {
      void client.invalidateQueries({ queryKey: ["admins"] });
      toast(t("network.managers.deleted"), {
        position: "bottom-right",
        type: "success",
      });
    },
    onError: (e: unknown) => {
      toast(
        e instanceof Error
          ? (e.message?.split(": ")[1] ?? t("network.errors.unknown"))
          : t("network.errors.unknown"),
        {
          position: "bottom-right",
          type: "error",
        }
      );
    },
  });

  return {
    ...mutation,
    mutate: (data: Admin) => mutation.mutate({ DeleteInput: { id: data.id } }),
  };
}

type TFilter = {
  filter: {
    rank: UserRank;
  };
};

function useGetAdminsByRanks(filter: TFilter) {
  return useFindAllAdminsQuery(filter, {
    queryKey: ["admins", Number(filter.filter.rank)],
    select: (data) => data.findAllAdmins,
  });
}

export { useDeleteAdmin, useGetAdminsByRanks, useUpdateAdmin };
